import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Button, Checkbox, Modal, MultiSelect, Select } from "../../shared";
import FileProcessor from "../../components/FileProcessor";
import { useImportCustomerCsvMutation } from "../../redux/services/customers/customersApi";

const ENTITY_CONFIGS = {
  clients: {
    label: "Clients",
    requiredFields: ["name"],
    columns: {
      name: { possibleHeaders: ["name", "client_name", "customer_name"] },
      email: { possibleHeaders: ["email", "client_email", "customer_email"] },
      phone: { possibleHeaders: ["phone", "client_phone", "customer_phone"] },
      coowner: { possibleHeaders: ["coowner", "owner", "contact"] },
      address: { possibleHeaders: ["address", "street", "address1"] },
      city: { possibleHeaders: ["city"] },
      state: { possibleHeaders: ["state"] },
      zipcode: { possibleHeaders: ["zipcode", "zip", "postal_code"] },
    },
  },
  locations: {
    label: "Locations",
    requiredFields: ["clientName", "address"],
    columns: {
      clientId: { possibleHeaders: ["client_id"] },
      clientName: { possibleHeaders: ["client_name", "customer_name", "client"] },
      clientEmail: { possibleHeaders: ["client_email"] },
      name: { possibleHeaders: ["location_name", "name"] },
      address: { possibleHeaders: ["address", "street", "address1"] },
      city: { possibleHeaders: ["city"] },
      state: { possibleHeaders: ["state"] },
      zipcode: { possibleHeaders: ["zipcode", "zip", "postal_code"] },
      phone: { possibleHeaders: ["phone", "location_phone"] },
      note: { possibleHeaders: ["note", "notes"] },
      precall: { possibleHeaders: ["precall", "pre_call"] },
    },
  },
  jobs: {
    label: "Jobs",
    requiredFields: ["clientName", "jobId"],
    columns: {
      clientId: { possibleHeaders: ["client_id"] },
      clientName: { possibleHeaders: ["client_name", "customer_name", "client"] },
      clientEmail: { possibleHeaders: ["client_email"] },
      clientPhone: { possibleHeaders: ["client_phone"] },
      jobId: { possibleHeaders: ["job_id", "jobid", "external_id"] },
      orderNumber: { possibleHeaders: ["order_number", "order_no"] },
      loadNumber: { possibleHeaders: ["load_number", "load_no"] },
      transactionDate: { possibleHeaders: ["transaction_date", "job_date", "date"] },
      appointmentDate: { possibleHeaders: ["appointment_date", "scheduled_date"] },
      status: { possibleHeaders: ["status"] },
      priority: { possibleHeaders: ["priority"] },
      note: { possibleHeaders: ["note", "notes"] },
      base: { possibleHeaders: ["base", "base_charge"] },
      fuelRate: { possibleHeaders: ["fuel_rate"] },
      flatRate: { possibleHeaders: ["flat_rate"] },
      useFlatRate: { possibleHeaders: ["use_flat_rate"] },
    },
  },
  employees_drivers: {
    label: "Employees / Drivers",
    requiredFields: ["fullName"],
    columns: {
      workerType: {
        possibleHeaders: ["worker_type", "type", "employee_type", "user_type"],
      },
      role: { possibleHeaders: ["role"] },
      fullName: { possibleHeaders: ["full_name", "name"] },
      firstName: { possibleHeaders: ["first_name"] },
      lastName: { possibleHeaders: ["last_name"] },
      email: { possibleHeaders: ["email"] },
      phone: { possibleHeaders: ["phone"] },
      department: { possibleHeaders: ["department"] },
      position: { possibleHeaders: ["position"] },
      isActive: { possibleHeaders: ["is_active", "active"] },
    },
  },
};

const ENTITY_OPTIONS = Object.entries(ENTITY_CONFIGS).map(([value, config]) => ({
  value,
  label: config.label,
}));
const METADATA_OPTIONS = [
  { value: "fileName", label: "File name" },
  { value: "headers", label: "Detected headers" },
  { value: "preview", label: "Preview counts" },
  { value: "columnMapping", label: "Column mapping" },
  { value: "customerId", label: "Customer id context" },
  { value: "unsyncedHeaders", label: "Unsynced headers" },
];

const formatEntityLabel = (entity) => ENTITY_CONFIGS[entity]?.label || entity;
const IGNORE_COLUMN = "__ignore";

const normalizeHeaderKey = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const tokenizeHeader = (value = "") =>
  String(value)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

const scoreHeaderSimilarity = (targetText = "", headerText = "") => {
  const targetNormalized = normalizeHeaderKey(targetText);
  const headerNormalized = normalizeHeaderKey(headerText);
  if (!targetNormalized || !headerNormalized) return 0;
  if (targetNormalized === headerNormalized) return 100;

  let score = 0;
  const targetTokens = tokenizeHeader(targetText);
  const headerTokens = tokenizeHeader(headerText);
  const targetSet = new Set(targetTokens);
  const headerSet = new Set(headerTokens);
  const overlapCount = [...targetSet].filter((token) => headerSet.has(token)).length;
  const overlapRatio = overlapCount / Math.max(targetSet.size, headerSet.size, 1);

  if (overlapCount > 0) {
    score += overlapRatio * 70;
  }

  if (
    targetNormalized.includes(headerNormalized) ||
    headerNormalized.includes(targetNormalized)
  ) {
    score += 20;
  }

  // Give a small bonus when first token aligns (often strongest CSV signal).
  if (targetTokens[0] && targetTokens[0] === headerTokens[0]) {
    score += 10;
  }

  return score;
};

const toReadableLabel = (field = "") =>
  String(field)
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());

export const inferHeaderMapping = (config = {}, headers = []) => {
  const columns = config?.columns || {};
  const normalizedHeaders = headers.map((header) => ({
    original: header,
    normalized: normalizeHeaderKey(header),
  }));
  const mapping = {};

  Object.entries(columns).forEach(([fieldKey, fieldConfig]) => {
    const possibleHeaders = fieldConfig?.possibleHeaders || [];
    const match = normalizedHeaders.find((header) =>
      possibleHeaders.some(
        (possibleHeader) => normalizeHeaderKey(possibleHeader) === header.normalized
      )
    );
    if (match?.original) {
      mapping[fieldKey] = match.original;
      return;
    }

    // Fuzzy fallback: use shared/similar words when no exact alias matched.
    const candidateTexts = [fieldKey, ...possibleHeaders];
    let bestHeader = null;
    let bestScore = 0;

    normalizedHeaders.forEach((header) => {
      const candidateScore = candidateTexts.reduce((maxScore, candidateText) => {
        const score = scoreHeaderSimilarity(candidateText, header.original);
        return Math.max(maxScore, score);
      }, 0);

      if (candidateScore > bestScore) {
        bestScore = candidateScore;
        bestHeader = header.original;
      }
    });

    // Conservative threshold to avoid incorrect auto-mapping.
    mapping[fieldKey] = bestScore >= 55 ? bestHeader : IGNORE_COLUMN;
  });

  return mapping;
};

export const buildRowsFromHeaderMapping = (rawRows = [], config = {}, mapping = {}) => {
  const columns = config?.columns || {};
  return rawRows.map((rawRow) => {
    const nextRow = {};
    Object.entries(columns).forEach(([fieldKey, fieldConfig]) => {
      const selectedHeader = mapping[fieldKey];
      if (!selectedHeader || selectedHeader === IGNORE_COLUMN) return;

      let value = rawRow?.[selectedHeader];
      if (value === undefined || value === null || value === "") return;

      if (typeof fieldConfig?.transform === "function") {
        try {
          value = fieldConfig.transform(value);
        } catch (_error) {
          // Keep original value if transform fails; user can still override mapping.
        }
      }
      nextRow[fieldKey] = value;
    });
    return nextRow;
  });
};

export const getUnsyncedHeaders = (headers = [], mapping = {}) => {
  const mappedHeaders = new Set(
    Object.values(mapping || {}).filter(
      (value) => value && value !== IGNORE_COLUMN && typeof value === "string"
    )
  );
  return headers.filter((header) => !mappedHeaders.has(header));
};

export const attachUnsyncedHeadersToRows = (
  rows = [],
  rawRows = [],
  unsyncedHeaders = []
) =>
  rows.map((row, index) => {
    const rawRow = rawRows[index] || {};
    const unsyncedHeaderValues = {};

    unsyncedHeaders.forEach((header) => {
      const value = rawRow?.[header];
      if (value === undefined || value === null || value === "") return;
      unsyncedHeaderValues[header] = value;
    });

    if (Object.keys(unsyncedHeaderValues).length === 0) {
      return row;
    }

    return {
      ...row,
      metadata: {
        ...(row?.metadata || {}),
        unsyncedHeaders: unsyncedHeaderValues,
      },
    };
  });

export const CustomerCsvImportModal = ({
  visible,
  setVisible,
  importAction,
  customerId,
  defaultEntity = "clients",
  title = "Import Customer Data",
}) => {
  const [entity, setEntity] = useState(defaultEntity);
  const [parsedRows, setParsedRows] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [isReady, setIsReady] = useState(false);
  const [result, setResult] = useState(null);
  const [headerMapping, setHeaderMapping] = useState({});
  const [metadataSelections, setMetadataSelections] = useState({
    fileName: true,
    headers: true,
    customerId: true,
    preview: true,
    columnMapping: true,
    unsyncedHeaders: false,
  });
  const [showAdvancedMetadata, setShowAdvancedMetadata] = useState(false);
  const [selectedUnsyncedHeaders, setSelectedUnsyncedHeaders] = useState([]);
  const [hasCustomUnsyncedSelection, setHasCustomUnsyncedSelection] = useState(false);
  const [importCustomerCsv, { isLoading: defaultImportLoading }] =
    useImportCustomerCsvMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setEntity(defaultEntity);
  }, [defaultEntity, visible]);

  const selectedConfig = useMemo(() => ENTITY_CONFIGS[entity], [entity]);
  const columnHeaders = useMemo(() => {
    if (!rawRows.length) return [];
    return Object.keys(rawRows[0] || {});
  }, [rawRows]);
  const rowsForImport = useMemo(() => {
    if (!rawRows.length) return parsedRows;
    return buildRowsFromHeaderMapping(rawRows, selectedConfig, headerMapping);
  }, [rawRows, parsedRows, selectedConfig, headerMapping]);
  const unsyncedHeaders = useMemo(
    () => getUnsyncedHeaders(columnHeaders, headerMapping),
    [columnHeaders, headerMapping]
  );
  const rowsWithUnsyncedMetadata = useMemo(
    () => attachUnsyncedHeadersToRows(rowsForImport, rawRows, selectedUnsyncedHeaders),
    [rowsForImport, rawRows, selectedUnsyncedHeaders]
  );
  const importRowsPayload = useMemo(
    () => (selectedUnsyncedHeaders.length > 0 ? rowsWithUnsyncedMetadata : rowsForImport),
    [rowsWithUnsyncedMetadata, rowsForImport, selectedUnsyncedHeaders]
  );

  const mappingOptions = useMemo(() => {
    const options = [{ value: IGNORE_COLUMN, label: "Ignore" }];
    return options.concat(
      columnHeaders.map((header) => ({
        value: header,
        label: header,
      }))
    );
  }, [columnHeaders]);
  const unsyncedHeaderOptions = useMemo(
    () =>
      unsyncedHeaders.map((header) => ({
        value: header,
        label: header,
      })),
    [unsyncedHeaders]
  );

  useEffect(() => {
    setSelectedUnsyncedHeaders((current) => {
      const stillAvailable = current.filter((header) => unsyncedHeaders.includes(header));
      if (hasCustomUnsyncedSelection) return stillAvailable;
      return unsyncedHeaders;
    });
  }, [unsyncedHeaders, hasCustomUnsyncedSelection]);

  const preview = useMemo(() => {
    const requiredFields = selectedConfig?.requiredFields || [];
    const sampleRows = importRowsPayload.slice(0, 5);
    let validRows = 0;

    importRowsPayload.forEach((row) => {
      const hasAllRequired = requiredFields.every((field) => {
        const value = row?.[field];
        return value !== undefined && value !== null && String(value).trim() !== "";
      });
      if (hasAllRequired) {
        validRows += 1;
      }
    });

    return {
      totalRows: importRowsPayload.length,
      validRows,
      invalidRows: Math.max(importRowsPayload.length - validRows, 0),
      sampleRows,
    };
  }, [importRowsPayload, selectedConfig]);

  const resetState = () => {
    setParsedRows([]);
    setRawRows([]);
    setFileName("");
    setIsReady(false);
    setResult(null);
    setHeaderMapping({});
    setMetadataSelections({
      fileName: true,
      headers: true,
      customerId: true,
      preview: true,
      columnMapping: true,
      unsyncedHeaders: false,
    });
    setShowAdvancedMetadata(false);
    setSelectedUnsyncedHeaders([]);
    setHasCustomUnsyncedSelection(false);
  };

  const closeModal = () => {
    resetState();
    setVisible(false);
  };

  const handleEntityChange = (event) => {
    setEntity(event.value);
    resetState();
  };

  const handleParsed = useCallback(
    (mappedRows, originalRows) => {
      const nextMappedRows = mappedRows || [];
      const nextRawRows = originalRows || [];
      setParsedRows(nextMappedRows);
      setRawRows(nextRawRows);
      setIsReady(Boolean(nextRawRows.length || nextMappedRows.length));
      const headers = Object.keys(nextRawRows?.[0] || {});
      const inferredMapping = inferHeaderMapping(selectedConfig, headers);
      const inferredUnsyncedHeaders = getUnsyncedHeaders(headers, inferredMapping);
      // Preserve manual mapping choices after first inference.
      setHeaderMapping((current) =>
        Object.keys(current || {}).length > 0 ? current : inferredMapping
      );
      setSelectedUnsyncedHeaders((current) =>
        current.length > 0
          ? current.filter((header) => inferredUnsyncedHeaders.includes(header))
          : inferredUnsyncedHeaders
      );
      setHasCustomUnsyncedSelection(false);
    },
    [selectedConfig]
  );

  const handleHeaderMappingChange = (event) => {
    if (!event?.id) return;
    setHeaderMapping((current) => ({
      ...current,
      [event.id]: event.value,
    }));
  };

  const toggleMetadataSelection = (event) => {
    if (!event?.id) return;
    setMetadataSelections((current) => ({
      ...current,
      [event.id]: Boolean(event?.checked),
    }));
  };

  const handleUnsyncedHeadersSelectionChange = (items = []) => {
    const nextHeaders = (items || [])
      .map((item) => item?.value)
      .filter((value) => typeof value === "string");
    setSelectedUnsyncedHeaders(nextHeaders);
    setHasCustomUnsyncedSelection(true);
  };

  const handleImport = async () => {
    if (!isReady) {
      toast("Upload and parse a CSV file before importing.", {
        type: "warning",
        toastId: "customer-import-missing-file",
      });
      return;
    }

    const includeMetadata = (key) => Boolean(metadataSelections[key]);
    const rowsPayload = importRowsPayload;

    const selectedMetadata = {};
    if (includeMetadata("fileName")) selectedMetadata.fileName = fileName;
    if (includeMetadata("headers")) selectedMetadata.headers = columnHeaders;
    if (includeMetadata("customerId")) selectedMetadata.customerId = customerId || undefined;
    if (includeMetadata("preview")) {
      selectedMetadata.preview = {
        totalRows: preview.totalRows,
        validRows: preview.validRows,
        invalidRows: preview.invalidRows,
      };
    }
    if (includeMetadata("columnMapping")) {
      selectedMetadata.columnMapping = headerMapping;
    }
    if (includeMetadata("unsyncedHeaders")) {
      selectedMetadata.unsyncedHeaders = selectedUnsyncedHeaders;
    }

    const payload = {
      entity,
      rows: rowsPayload,
      metadata: selectedMetadata,
    };

    try {
      setIsSubmitting(true);
      const response = importAction
        ? await importAction(payload).unwrap()
        : await importCustomerCsv(payload).unwrap();

      setResult(response.summary || null);
      toast(response.message || "Import completed", {
        type: "success",
        toastId: "customer-import-success",
      });
    } catch (error) {
      toast(error?.data?.message || "Failed to import CSV rows", {
        type: "error",
        toastId: "customer-import-error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelect = useCallback((file) => {
    setFileName(file?.name || "");
    setResult(null);
  }, []);

  const handleParseError = useCallback((error) => {
    toast(error?.message || "File parsing failed", {
      type: "error",
      toastId: "customer-import-parse-error",
    });
    setIsReady(false);
  }, []);

  const isLoading = isSubmitting || defaultImportLoading;
  const footer = (
    <div>
      <Button type="alternative" className="mr-2" onClick={closeModal}>
        Cancel
      </Button>
      <Button loading={isLoading} disabled={isLoading || !isReady} onClick={handleImport}>
        Import {formatEntityLabel(entity)}
      </Button>
    </div>
  );

  const content = (
    <div className="space-y-4">
      <Select
        id="entity"
        label="Import Target"
        value={formatEntityLabel(entity)}
        onChange={handleEntityChange}
        options={ENTITY_OPTIONS}
      />

      <div className="rounded-md border border-gray-200 p-3">
        <div className="text-sm text-gray-700 mb-2">
          Upload CSV / Excel data for {formatEntityLabel(entity)}.
        </div>
        <FileProcessor
          config={selectedConfig}
          acceptedFileTypes={[".csv", ".xlsx", ".xls"]}
          showPreview={false}
          showFileViewer={false}
          onFileSelect={handleFileSelect}
          onDataParsed={handleParsed}
          onError={handleParseError}
        />
      </div>

      {columnHeaders.length > 0 && (
        <div className="rounded-md border border-gray-200 p-3 bg-gray-50">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            Detected Columns
          </div>
          <div className="text-sm text-gray-700 break-words">
            {columnHeaders.join(", ")}
          </div>
        </div>
      )}

      {columnHeaders.length > 0 && (
        <div className="rounded-md border border-gray-200 p-3">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
            Column Mapping
          </div>
          <div className="text-sm text-gray-600 mb-3">
            Map file headers to model fields when names differ.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.keys(selectedConfig?.columns || {}).map((fieldKey) => (
              <Select
                key={fieldKey}
                id={fieldKey}
                label={toReadableLabel(fieldKey)}
                value={
                  headerMapping[fieldKey] && headerMapping[fieldKey] !== IGNORE_COLUMN
                    ? headerMapping[fieldKey]
                    : "Ignore"
                }
                options={mappingOptions}
                onChange={handleHeaderMappingChange}
              />
            ))}
          </div>
          {unsyncedHeaders.length > 0 && (
            <div className="mt-3">
              <MultiSelect
                id="unsyncedHeaders"
                label="Unsynced headers to include per item metadata"
                placeholder="Select unsynced headers"
                selectionNoun="headers"
                options={unsyncedHeaderOptions}
                value={selectedUnsyncedHeaders}
                onChange={handleUnsyncedHeadersSelectionChange}
                search={true}
                searchKeys={["label", "value"]}
              />
            </div>
          )}
        </div>
      )}

      <div className="rounded-md border border-gray-200 p-3">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wide text-gray-500">
            Upload Metadata
          </div>
          <button
            type="button"
            onClick={() => setShowAdvancedMetadata((current) => !current)}
            className="text-xs text-primary-600 hover:text-primary-700"
          >
            {showAdvancedMetadata ? "Hide Advanced Settings" : "Show Advanced Settings"}
          </button>
        </div>
        {showAdvancedMetadata && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {METADATA_OPTIONS.map((option) => (
              <Checkbox
                key={option.value}
                id={option.value}
                checked={Boolean(metadataSelections[option.value])}
                onChange={toggleMetadataSelection}
                label={option.label}
              />
            ))}
          </div>
        )}
      </div>

      {preview.totalRows > 0 && (
        <div className="rounded-md border border-gray-200 p-3">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
            Preview
          </div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-gray-500">Rows</div>
              <div className="font-semibold">{preview.totalRows}</div>
            </div>
            <div>
              <div className="text-gray-500">Valid</div>
              <div className="font-semibold text-green-700">{preview.validRows}</div>
            </div>
            <div>
              <div className="text-gray-500">Needs review</div>
              <div className="font-semibold text-amber-700">{preview.invalidRows}</div>
            </div>
          </div>
          {preview.sampleRows.length > 0 && (
            <pre className="mt-3 text-xs bg-gray-50 p-2 rounded overflow-auto max-h-40">
              {JSON.stringify(preview.sampleRows, null, 2)}
            </pre>
          )}
        </div>
      )}

      {result && (
        <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm">
          <div className="font-semibold mb-1">Import Result</div>
          <div>Created: {result.created || 0}</div>
          <div>Updated: {result.updated || 0}</div>
          <div>Skipped: {result.skipped || 0}</div>
          <div>Errors: {result.errors?.length || 0}</div>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      visible={visible}
      setVisible={setVisible}
      onClose={closeModal}
      title={title}
      size="lg"
      footer={footer}
      footerPosition="right"
      content={content}
      contentWrapperclassName="p-3 lg:p-6"
    />
  );
};
